if (!customElements.get('ff-pack-finder')) {
  class FfPackFinder extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized) return;
      this.dataset.initialized = 'loading';

      this.panel = this.querySelector('[data-ff-pack-panel]');
      this.toggleButtons = Array.from(this.querySelectorAll('[data-ff-pack-toggle]'));
      this.washesInput = this.querySelector('[data-ff-washes]');
      this.washesOutput = this.querySelector('[data-ff-washes-output]');
      this.doseInputs = Array.from(this.querySelectorAll('[data-ff-dose]'));
      this.householdInputs = Array.from(this.querySelectorAll('[data-ff-households]'));
      this.result = this.querySelector('[data-ff-result]');
      this.unavailable = this.querySelector('[data-ff-unavailable]');
      this.resultTitle = this.querySelector('[data-ff-result-title]');
      this.durationOutput = this.querySelector('[data-ff-duration]');
      this.washPriceOutput = this.querySelector('[data-ff-wash-price]');
      this.householdPriceOutput = this.querySelector('[data-ff-household-price]');
      this.savingsPanel = this.querySelector('[data-ff-savings]');
      this.householdSavingOutput = this.querySelector('[data-ff-household-saving]');
      this.totalSavingOutput = this.querySelector('[data-ff-total-saving]');
      this.selectButton = this.querySelector('[data-ff-select]');
      this.alternativeButton = this.querySelector('[data-ff-alternative]');
      this.alternativeCopy = this.querySelector('[data-ff-alternative-copy]');
      this.sharePanel = this.querySelector('[data-ff-share-panel]');
      this.shareLinks = Array.from(this.querySelectorAll('[data-ff-share]'));
      this.shareStatus = this.querySelector('[data-ff-share-status]');
      this.locale = this.normaliseLocale(this.dataset.locale);
      this.currency = this.dataset.currency || 'EUR';
      this.variants = this.readVariants();
      this.packages = this.buildPackages(this.variants);
      this.recommendation = null;
      this.alternative = null;

      this.toggleButtons.forEach((button) => button.addEventListener('click', () => this.togglePanel(button)));
      this.washesInput?.addEventListener('input', () => this.update());
      [...this.doseInputs, ...this.householdInputs].forEach((input) =>
        input.addEventListener('change', () => this.update())
      );
      this.selectButton?.addEventListener('click', () => this.selectRecommendation());
      this.alternativeButton?.addEventListener('click', () => this.selectAlternative());
      this.shareLinks.forEach((control) =>
        control.addEventListener('click', (event) => this.share(event, control.dataset.ffShare))
      );

      this.update();
      this.toggleButtons.forEach((button) => {
        button.disabled = false;
      });
      this.dataset.initialized = 'true';
    }

    normaliseLocale(locale) {
      if (locale === 'fr') return 'fr-FR';
      if (locale === 'en') return 'en-GB';
      return 'nl-NL';
    }

    readVariants() {
      try {
        return JSON.parse(this.querySelector('[data-ff-variants]')?.textContent || '[]');
      } catch (error) {
        return [];
      }
    }

    getPackQuantity(variant) {
      const searchableText = [variant.title, ...(variant.options || [])].join(' ');
      const match = searchableText.match(/(?:^|\D)(100|300|800)(?:\D|$)/);
      return match ? Number(match[1]) : 0;
    }

    buildPackages(variants) {
      const packages = new Map();

      variants.forEach((variant) => {
        const quantity = this.getPackQuantity(variant);
        if (!quantity) return;

        const current = packages.get(quantity);
        if (!current || (!current.available && variant.available)) {
          packages.set(quantity, { ...variant, quantity });
        }
      });

      return Array.from(packages.values()).sort((a, b) => a.quantity - b.quantity);
    }

    togglePanel(trigger) {
      if (!this.panel) return;
      const willOpen = this.panel.hidden;
      this.panel.hidden = !willOpen;
      this.toggleButtons.forEach((button) => {
        if (button.hasAttribute('aria-expanded')) button.setAttribute('aria-expanded', String(willOpen));
      });

      if (willOpen) {
        this.update();
        this.track('ff_pack_finder_open');
        window.requestAnimationFrame(() => this.washesInput?.focus({ preventScroll: true }));
      } else if (!trigger?.hasAttribute('aria-expanded')) {
        const opener = this.querySelector('[data-ff-pack-toggle][aria-expanded]');
        window.requestAnimationFrame(() => opener?.focus({ preventScroll: true }));
      }
    }

    getInputValue(inputs, fallback) {
      return Number(inputs.find((input) => input.checked)?.value || fallback);
    }

    update() {
      const washes = Math.max(1, Number(this.washesInput?.value || 1));
      const dose = this.getInputValue(this.doseInputs, 1);
      const households = this.getInputValue(this.householdInputs, 1);

      if (this.washesOutput) {
        const washLabel = washes === 1 ? this.dataset.washOne : this.dataset.washMany;
        this.washesOutput.textContent = `${washes} ${washLabel}`;
      }

      const availablePackages = this.packages.filter((pack) => pack.available);
      if (!availablePackages.length) {
        this.showUnavailable();
        return;
      }

      const annualCapsules = washes * dose * households * 52;
      this.recommendation = availablePackages.reduce((bestPack, pack) => {
        const bestGap = Math.abs(bestPack.quantity - annualCapsules);
        const packGap = Math.abs(pack.quantity - annualCapsules);
        const bestScore = bestGap * (bestPack.quantity < annualCapsules ? 1.5 : 1);
        const packScore = packGap * (pack.quantity < annualCapsules ? 1.5 : 1);
        return packScore < bestScore ? pack : bestPack;
      });

      const weeks = this.recommendation.quantity / (washes * dose * households);
      const duration = this.formatDuration(weeks);
      const pricePerCapsule = this.recommendation.price / this.recommendation.quantity;
      const householdPrice = this.recommendation.price / households;

      this.result.hidden = false;
      this.unavailable.hidden = true;
      this.resultTitle.textContent = `${this.recommendation.quantity} capsules`;
      this.durationOutput.textContent = `${this.dataset.durationPrefix} ${duration}`;
      this.washPriceOutput.textContent = this.formatMoney(pricePerCapsule);
      this.householdPriceOutput.textContent = this.formatMoney(householdPrice);
      this.selectButton.disabled = false;
      this.selectButton.textContent = this.template(this.dataset.selectTemplate, {
        quantity: this.recommendation.quantity,
      });

      this.updateSaving(households);
      this.updateShare(duration, households, householdPrice);
      this.updateAlternative(availablePackages);
    }

    showUnavailable() {
      this.recommendation = null;
      this.alternative = null;
      if (this.result) this.result.hidden = true;
      if (this.unavailable) this.unavailable.hidden = false;
      if (this.selectButton) this.selectButton.disabled = true;
      if (this.alternativeButton) this.alternativeButton.hidden = true;
    }

    updateAlternative(availablePackages) {
      if (!this.recommendation || !this.alternativeButton || !this.alternativeCopy) return;

      const lowerPackages = availablePackages.filter((pack) => pack.quantity < this.recommendation.quantity);
      const higherPackages = availablePackages.filter((pack) => pack.quantity > this.recommendation.quantity);
      const useLowerPrice = lowerPackages.length > 0;
      this.alternative = useLowerPrice
        ? lowerPackages[lowerPackages.length - 1]
        : higherPackages[0];

      if (!this.alternative) {
        this.alternativeButton.hidden = true;
        return;
      }

      const template = useLowerPrice
        ? this.dataset.alternativeLowerTemplate
        : this.dataset.alternativeLongerTemplate;
      this.alternativeCopy.textContent = this.template(template, { quantity: this.alternative.quantity });
      this.alternativeButton.hidden = false;
    }

    updateSaving(households) {
      const basePack = this.packages.find((pack) => pack.quantity === 100 && pack.available);
      if (!basePack || !this.recommendation || !this.savingsPanel) {
        if (this.savingsPanel) this.savingsPanel.hidden = true;
        return;
      }

      const basePrice = basePack.price * (this.recommendation.quantity / 100);
      const totalSaving = Math.max(0, basePrice - this.recommendation.price);
      const householdSaving = totalSaving / Math.max(1, households);

      this.householdSavingOutput.textContent = this.formatMoney(householdSaving);
      this.totalSavingOutput.textContent = this.formatMoney(totalSaving);
      this.savingsPanel.hidden = false;
    }

    updateShare(duration, households, householdPrice) {
      if (!this.sharePanel || !this.recommendation) {
        if (this.sharePanel) this.sharePanel.hidden = true;
        return;
      }

      const householdLabel = households === 1 ? this.dataset.householdOne : this.dataset.householdMany;
      const message = this.template(this.dataset.shareTemplate, {
        quantity: this.recommendation.quantity,
        duration,
        households: `${households} ${householdLabel}`,
        price: this.formatMoney(householdPrice),
      });
      const pageUrl = `${window.location.origin}${window.location.pathname}`;
      this.shareData = { title: this.dataset.shareTitle, text: message, url: pageUrl };
      const targets = {
        whatsapp: `https://wa.me/?text=${encodeURIComponent(`${message} ${pageUrl}`)}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`,
        x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(pageUrl)}`,
        email: `mailto:?subject=${encodeURIComponent(this.shareData.title)}&body=${encodeURIComponent(`${message}\n\n${pageUrl}`)}`,
      };
      Object.entries(targets).forEach(([platform, href]) => {
        const link = this.querySelector(`[data-ff-share="${platform}"]`);
        if (!link) return;
        link.href = href;
        if (platform !== 'email') {
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
        }
      });
      this.sharePanel.hidden = false;
    }

    async share(event, platform) {
      if (!this.shareData) {
        event.preventDefault();
        return;
      }
      this.track('ff_pack_finder_share', { platform });
      if (['whatsapp', 'facebook', 'x', 'email'].includes(platform)) return;
      event.preventDefault();
      try {
        if (platform === 'native' && navigator.share) {
          await navigator.share(this.shareData);
          return;
        }
        const value = `${this.shareData.text} ${this.shareData.url}`;
        if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(value);
        else {
          const input = document.createElement('textarea');
          input.value = value;
          input.setAttribute('readonly', '');
          input.style.position = 'fixed';
          input.style.opacity = '0';
          document.body.appendChild(input);
          input.select();
          document.execCommand('copy');
          input.remove();
        }
        if (this.shareStatus) {
          this.shareStatus.textContent = this.dataset.shareCopied;
          this.shareStatus.hidden = false;
        }
      } catch (error) {
        if (error?.name !== 'AbortError' && this.shareStatus) {
          this.shareStatus.textContent = this.dataset.shareFailed;
          this.shareStatus.hidden = false;
        }
      }
    }

    formatDuration(weeks) {
      if (weeks < 6) {
        const roundedWeeks = Math.max(1, Math.round(weeks));
        const label = roundedWeeks === 1 ? this.dataset.weekOne : this.dataset.weekMany;
        return `${roundedWeeks} ${label}`;
      }

      const roundedMonths = Math.max(1, Math.round(weeks / 4.345));
      const label = roundedMonths === 1 ? this.dataset.monthOne : this.dataset.monthMany;
      return `${roundedMonths} ${label}`;
    }

    formatMoney(cents) {
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(cents / 100);
      } catch (error) {
        return `${(cents / 100).toFixed(2)} ${this.currency}`;
      }
    }

    template(value, replacements) {
      return Object.entries(replacements).reduce(
        (output, [key, replacement]) => output.replaceAll(`[${key}]`, String(replacement)),
        value || ''
      );
    }

    selectRecommendation() {
      if (!this.recommendation) return;
      this.selectPackage(this.recommendation, this.selectButton, 'ff_pack_finder_select');
    }

    selectAlternative() {
      if (!this.alternative) return;
      this.selectPackage(this.alternative, this.alternativeButton, 'ff_pack_finder_alternative_select');
    }

    selectPackage(selectedPackage, triggerButton, eventName) {
      const variantPicker = document.querySelector(`variant-selects[data-section="${CSS.escape(this.dataset.sectionId)}"]`);
      if (!variantPicker) return;

      const optionGroups = Array.from(
        variantPicker.querySelectorAll('fieldset, .product-form__input--dropdown')
      ).filter((group) => group.closest('variant-selects') === variantPicker);
      let changedControl = null;

      selectedPackage.options.forEach((optionValue, index) => {
        const group = optionGroups[index];
        if (!group) return;

        if (group.tagName === 'FIELDSET') {
          const radio = Array.from(group.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === optionValue
          );
          if (radio) {
            radio.checked = true;
            changedControl = radio;
          }
        } else {
          const select = group.querySelector('select');
          if (select) {
            select.value = optionValue;
            changedControl = select;
          }
        }
      });

      if (!changedControl) return;
      changedControl.dispatchEvent(new Event('change', { bubbles: true }));
      const selectedText = this.template(this.dataset.selectedTemplate, {
        quantity: selectedPackage.quantity,
      });
      if (triggerButton === this.alternativeButton && this.alternativeCopy) {
        this.alternativeCopy.textContent = selectedText;
      } else {
        triggerButton.textContent = selectedText;
      }
      this.track(eventName, {
        variant_id: selectedPackage.id,
        pack_quantity: selectedPackage.quantity,
      });

      window.setTimeout(() => {
        const addButton = document.getElementById(`ProductSubmitButton-${this.dataset.sectionId}`);
        if (!addButton) return;
        addButton.scrollIntoView({ behavior: 'smooth', block: 'center' });
        window.setTimeout(() => addButton.focus({ preventScroll: true }), 350);
      }, 250);
    }

    track(event, details = {}) {
      if (!Array.isArray(window.dataLayer)) return;
      window.dataLayer.push({ event, ...details });
    }
  }

  customElements.define('ff-pack-finder', FfPackFinder);
}
